import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, AlertTriangle, Shield, HardDrive, BookOpen, PlayCircle, Trash2, BarChart2 } from 'lucide-react';
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from '../components/ui/alert-dialog';

const APP_STORAGE_KEYS = [
  'emergency_inventory_kits',
  'inventory_household_size',
  'inventory_custom_units',
  'inventory_templates',
  'document_audit_data',
  'document_uploads',
  'document_custom_items',
  'document_custom_sections',
  'document_section_order',
  'document_item_order',
  'document_hidden_items',
  'emergency_plan_data',
  'review_reminder_dismissed',
  'document_audit_pets',
  'map_index_data',
  'admin_token',
];

const ToolHub = () => {
  const [pulsing, setPulsing] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setPulsing(false), 5000);
    return () => clearTimeout(timer);
  }, []);

  const tools = [
    {
      id: 'emergency',
      title: 'Emergency Contact Sheet Generator',
      description:
        'Create comprehensive emergency contact sheets with professional templates for home, travel, and wallet-sized cards.',
      icon: '🚨',
      path: '/emergency',
      features: [
        'Pre-loaded with 140+ emergency contacts',
        'Master List, Travel, and Wallet templates',
        'Drag & drop organization',
        'Import/Export functionality',
        'Print-ready formats',
      ],
      className: 'emergency',
    },
    {
      id: 'maintenance',
      title: 'Home Maintenance List Generator',
      description:
        'Smart monthly maintenance planning with seasonal task scheduling and completion tracking.',
      icon: '🏠',
      path: '/maintenance',
      features: [
        'Month-by-month maintenance schedule',
        'Pre-loaded with professional tasks',
        'Progress tracking dashboard',
        'Seasonal task automation',
        'Printable schedules',
      ],
      className: 'maintenance',
    },
    {
      id: 'budget',
      title: 'Budget Creation Tool',
      description:
        'Create comprehensive budgets to determine free cash flow for debt payments. Includes emergency fund planning and expense tracking.',
      icon: '💰',
      path: '/budget',
      features: [
        'Emergency fund calculator ($1000 × family age)',
        'Immediate emergency cash tracking ($500 minimum)',
        'Comprehensive expense categories',
        'Visual budget breakdown charts',
        'Free cash flow analysis for debt payments',
      ],
      className: 'budget',
    },
    {
      id: 'debt-test',
      title: 'Debt Management Tool',
      description:
        'Test the sophisticated debt optimization and loan amortization calculations with your custom efficiency algorithm.',
      icon: '🧮',
      path: '/debt-test',
      features: [
        'Loan amortization calculations',
        'Balanced efficiency scoring algorithm',
        'Multi-debt portfolio optimization',
        'Cashflow recapture strategy',
        'Goal setting and scenario analysis',
      ],
      className: 'debt-test',
    },
    {
      id: 'inventory',
      title: 'Emergency Supply Inventory',
      description:
        'Track emergency kits and supplies with dual kit/master inventory views. Monitor expirations and maintain audit checklists.',
      icon: '📦',
      path: '/inventory',
      features: [
        'Kit-based organization with preset templates',
        'Master inventory view across all kits',
        'Expiration tracking with visual warnings',
        'Audit checklists and completion tracking',
        'Import/Export and print-ready reports',
      ],
      className: 'inventory',
    },
    {
      id: 'emergency-plan',
      title: 'Emergency Plan Creator',
      description:
        'Focused emergency planning: family, evacuation, disaster plans, communication, medical, home, pets, grab-and-go, school/work. Export to add to your binder.',
      icon: '📋',
      path: '/emergency-plan',
      features: [
        'Family info, evacuation routes, disaster-specific plans',
        'Communication plan, medical, home, pets (with photo & vet info)',
        'Grab-and-go checklist, school & work plans',
        'Export — use in Build your binder',
      ],
      className: 'emergency-plan',
      status: 'testing',
    },
    {
      id: 'documents',
      title: 'Important Documents',
      description:
        'Document audit checklist. Mark items Physical/Digital/Missing, get a prioritized task list, then export for your binder.',
      icon: '📄',
      path: '/documents',
      features: [
        '8-section document checklist + prioritized task list',
        'Mark each item Physical, Digital, or Missing',
        'Export for binder',
      ],
      className: 'documents',
      status: 'early development',
    },
    {
      id: 'map-index',
      title: 'Map Index',
      description:
        'Curated map resources with links and instructions. Optionally add your location so your binder includes coordinates to use in any map.',
      icon: '🗺️',
      path: '/map-index',
      features: [
        'Curated free maps: OpenTopoMap, OpenRailwayMap, USGS, OpenStreetMap',
        'Instructions for each resource',
        'Optional address lookup → coordinates for your binder',
        'Export for binder',
      ],
      className: 'map-index',
      status: 'early development',
    },
    {
      id: 'financial-summary',
      title: 'Financial & Insurance Summary',
      description:
        'Binder-safe financial summary: emergency fund, insurance, debt overview. No account numbers.',
      icon: '💰',
      path: '/financial-summary',
      features: [
        'Emergency fund summary',
        'Insurance (type + company)',
        'Debt overview',
        'Export',
      ],
      className: 'financial-summary',
      status: 'early development',
    },
    {
      id: 'valuables',
      title: 'Valuables Inventory',
      description:
        'List high-value items for insurance documentation. Export for your preparedness binder.',
      icon: '🏆',
      path: '/valuables',
      features: [
        'Item name, category, value',
        'Location or photo notes',
        'Export',
      ],
      className: 'valuables',
      status: 'early development',
    },
    {
      id: 'babysitter-reference',
      title: 'Babysitter Quick Reference',
      description:
        'Generate a printable quick reference card with essential emergency info for babysitters.',
      icon: '👶',
      path: '/babysitter-reference',
      features: [
        'Emergency numbers and address',
        'Parent and contact info',
        'Print-ready reference card',
      ],
      className: 'babysitter-reference',
      status: 'early development',
    },
    {
      id: 'binder-hub',
      title: 'Build your binder',
      description:
        'Upload exports from each tool, organize sections as you like, and compile one cohesive binder with table of contents.',
      icon: '📚',
      path: '/binder-hub',
      features: [
        'Upload one or more export files from the tools',
        'Reorder, remove, or rename sections',
        'Generate one HTML binder (cover, TOC, all sections)',
        'Binder care tips',
      ],
      className: 'binder-hub',
      status: 'early development',
    },
  ];

  return (
    <div className="hub-container">
      <div className="hub-header fade-in">
        <h1 className="hub-title">🛠️ Preparedness Tool Hub</h1>
        <p className="hub-subtitle">
          Access all your maintenance and emergency preparedness tools in one place
        </p>
      </div>

      {/* Privacy & Getting Started Section */}
      <div className="fade-in" style={{ animationDelay: '0.1s' }}>
        <div className="max-w-4xl mx-auto mb-8 space-y-4 px-4">
          {/* Privacy Notice */}
          <div className="rounded-xl border border-theme-accent/30 bg-theme-accent/5 p-5">
            <div className="flex items-start gap-3">
              <Shield className="w-6 h-6 text-theme-accent flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-bold text-text-primary mb-1">Your privacy is built in</h3>
                <p className="text-sm text-text-muted leading-relaxed">
                  Your data never leaves your browser. Everything is stored locally in your browser's storage
                  — we don't collect, transmit, or store any of your information on our servers. We do this
                  to protect your privacy, keep costs down, and because we don't want to be responsible for
                  securing your sensitive data.
                </p>
              </div>
            </div>
          </div>

          {/* Save/Load Notice */}
          <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-5">
            <div className="flex items-start gap-3">
              <HardDrive className="w-6 h-6 text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-bold text-text-primary mb-1">Export = Save, Import = Load</h3>
                <p className="text-sm text-text-muted leading-relaxed">
                  Your data lives in your browser's storage, which means it can be lost if you clear your
                  browser data or switch devices. To save your progress, use the Export button in each tool
                  to download your data to your own machine. To pick up where you left off, use Import to
                  load that file back in.
                </p>
              </div>
            </div>
          </div>

          {/* Clear Data + Getting Started Buttons */}
          <div className="flex justify-center gap-3 flex-wrap">
            <Dialog onOpenChange={(open) => { if (open) setPulsing(false); }}>
              <DialogTrigger asChild>
                <button
                  className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-theme-accent/20 text-theme-accent hover:bg-theme-accent/30 font-medium transition-colors text-base ${pulsing ? 'animate-pulse' : ''}`}
                >
                  <BookOpen className="w-5 h-5" />
                  Getting Started
                </button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-xl">Getting Started</DialogTitle>
                  <DialogDescription>
                    Here's how to get the most out of the Preparedness Tool Hub.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 mt-2">
                  <ol className="list-decimal list-inside space-y-3 text-sm text-text-primary">
                    <li className="leading-relaxed">
                      <span className="font-medium">Explore the tools</span> — Browse the cards below and open any tool that fits your needs. Each tool focuses on one piece of your preparedness plan.
                    </li>
                    <li className="leading-relaxed">
                      <span className="font-medium">Fill in your data</span> — Work through each tool at your own pace. Your data is saved automatically in your browser.
                    </li>
                    <li className="leading-relaxed">
                      <span className="font-medium">Export to save</span> — Use the Export button in each tool to download your data as a file. This is your backup — keep it safe.
                    </li>
                    <li className="leading-relaxed">
                      <span className="font-medium">Build your binder</span> — When you're ready, head to "Build your binder" to combine your exports into one printable document with a cover page and table of contents.
                    </li>
                  </ol>

                  {/* Video placeholder */}
                  <div className="aspect-video rounded-lg border-2 border-dashed border-gray-600 flex flex-col items-center justify-center text-text-muted">
                    <PlayCircle className="w-12 h-12 mb-2 opacity-40" />
                    <p className="text-sm font-medium">Hub overview video coming soon</p>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 font-medium transition-colors text-base"
                >
                  <Trash2 className="w-5 h-5" />
                  Clear All My Data
                </button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Clear all data?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will permanently delete all of your data stored in this browser, including
                    inventory, documents, emergency plans, maps, and all other tool data. This action
                    cannot be undone. Make sure you've exported anything you want to keep.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    className="bg-red-600 hover:bg-red-700 text-white"
                    onClick={() => {
                      APP_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
                      window.location.reload();
                    }}
                  >
                    Yes, delete everything
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </div>

      <div className="tools-grid">
        {tools.slice(0, 4).map((tool, index) => (
          <Link
            key={tool.id}
            to={tool.path}
            className="tool-card fade-in"
            style={{ animationDelay: `${index * 0.1}s` }}
          >
            <div className="tool-header flex items-start gap-4 mb-4">
              <div className="tool-icon text-3xl" aria-hidden>
                {tool.icon}
              </div>
              <div className="flex-1 min-w-0">
                <div className="tool-title text-xl font-bold">{tool.title}</div>
                <div className="mt-2">
                  <span
                    className={`tool-status inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${
                      tool.status === 'testing'
                        ? 'bg-amber-500/20 text-amber-400'
                        : tool.status === 'early development'
                          ? 'bg-red-500/20 text-red-400'
                          : 'bg-theme-accent/20 text-theme-accent'
                    }`}
                  >
                    {tool.status === 'testing' && '🧪 Testing'}
                    {tool.status === 'early development' && (
                      <>
                        <AlertTriangle className="h-4 w-4 shrink-0" />
                        Early development
                      </>
                    )}
                    {tool.status !== 'testing' && tool.status !== 'early development' && '✓ Ready'}
                  </span>
                </div>
              </div>
            </div>
            <div className="tool-description mb-5 leading-relaxed">{tool.description}</div>
            <ul className="tool-features list-none mb-5 space-y-2">
              {tool.features.map((feature, i) => (
                <li key={i} className="pl-5 relative">
                  <span className="absolute left-0 text-theme-accent font-semibold">✓</span>
                  {feature}
                </li>
              ))}
            </ul>
            <div className="tool-cta flex items-center gap-2 font-medium">
              Open Tool <ExternalLink size={16} />
            </div>
          </Link>
        ))}
      </div>

      <div className="risk-cta-banner">
        <div className="flex items-center gap-4 flex-wrap">
          <BarChart2 className="w-8 h-8 text-primary flex-shrink-0" />
          <div className="flex-1 min-w-[200px]">
            <h3 className="font-bold text-text-primary text-lg mb-1">Know Your Risks Before You Plan</h3>
            <p className="text-sm text-text-muted leading-relaxed">
              Find out which emergencies are most likely where you live — so you know what to prepare for first.
            </p>
          </div>
          <a
            href="https://wahletec.com/products/personalized-location-risk-report"
            target="_blank"
            rel="noopener noreferrer"
            className="px-6 py-3 bg-primary text-primary-foreground rounded-lg font-semibold hover:opacity-90 shadow-sm flex items-center gap-2 whitespace-nowrap"
          >
            Get Your Risk Report <ExternalLink size={16} />
          </a>
        </div>
      </div>

      <div className="tools-grid">
        {tools.slice(4).map((tool, index) => (
          <Link
            key={tool.id}
            to={tool.path}
            className="tool-card fade-in"
            style={{ animationDelay: `${(index + 4) * 0.1}s` }}
          >
            <div className="tool-header flex items-start gap-4 mb-4">
              <div className="tool-icon text-3xl" aria-hidden>
                {tool.icon}
              </div>
              <div className="flex-1 min-w-0">
                <div className="tool-title text-xl font-bold">{tool.title}</div>
                <div className="mt-2">
                  <span
                    className={`tool-status inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${
                      tool.status === 'testing'
                        ? 'bg-amber-500/20 text-amber-400'
                        : tool.status === 'early development'
                          ? 'bg-red-500/20 text-red-400'
                          : 'bg-theme-accent/20 text-theme-accent'
                    }`}
                  >
                    {tool.status === 'testing' && '🧪 Testing'}
                    {tool.status === 'early development' && (
                      <>
                        <AlertTriangle className="h-4 w-4 shrink-0" />
                        Early development
                      </>
                    )}
                    {tool.status !== 'testing' && tool.status !== 'early development' && '✓ Ready'}
                  </span>
                </div>
              </div>
            </div>
            <div className="tool-description mb-5 leading-relaxed">{tool.description}</div>
            <ul className="tool-features list-none mb-5 space-y-2">
              {tool.features.map((feature, i) => (
                <li key={i} className="pl-5 relative">
                  <span className="absolute left-0 text-theme-accent font-semibold">✓</span>
                  {feature}
                </li>
              ))}
            </ul>
            <div className="tool-cta flex items-center gap-2 font-medium">
              Open Tool <ExternalLink size={16} />
            </div>
          </Link>
        ))}
      </div>

      <div className="hub-footer">
        <p>Professional preparedness tools by Wahle-Tec</p>
      </div>
    </div>
  );
};

export default ToolHub;
